<!-- Navbar -->
<nav class="fixed top-0 w-full z-50 glass border-b border-slate-100/50 dark:border-slate-700/50 transition-colors duration-300">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex justify-between items-center h-20">
      <!-- Logo -->
      <div class="flex gap-x-2 gap-y-2 items-center">
        <a routerLink="/" class="flex items-center gap-2">
          <img src="assets/img/creditpathlogo.webp" alt="CreditPath Logo" class="w-6 h-6">
          <span class="text-[17px] font-semibold tracking-tighter lowercase text-slate-900 dark:text-white transition-colors" style="font-family: 'Geist', sans-serif;">
            creditpath.io
          </span>
        </a>
      </div>

      <!-- Desktop Nav -->
      <div class="hidden md:flex items-center gap-8">
        <a routerLink="/" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
          Home
        </a>
        <button (click)="scrollToSection('solutions')" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white cursor-pointer">
          Credit Solutions
        </button>
        <button (click)="scrollToSection('pricing')" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white cursor-pointer">
          Pricing
        </button>
        <button (click)="scrollToSection('about')" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white cursor-pointer">
          About
        </button>
        <button (click)="scrollToSection('contact')" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white cursor-pointer">
          Contact
        </button>
        <!-- Request Terms - hidden for lenders unless admin -->
        @if (showRequestTerms) {
          <button (click)="navigateToRequestTerms()" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white cursor-pointer">
            Request Terms
          </button>
        }
        <!-- Advisor Portal - shown for advisors or admins -->
        @if (showAdvisorPortal) {
          <a routerLink="/advisor-portal" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
            Advisor Portal
          </a>
        }
        <!-- Lender Portal - shown for lenders or admins -->
        @if (showLenderPortal) {
          <a routerLink="/lender-portal" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
            Lender Portal
          </a>
        }
      </div>

      <!-- Actions -->
      <div class="hidden md:flex items-center gap-4">
        <!-- Theme Toggle -->
        <button
          (click)="toggleTheme()"
          class="p-2 rounded-full transition-all hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
          [attr.aria-label]="isDarkMode() ? 'Switch to light mode' : 'Switch to dark mode'">
          <!-- Sun icon (shown in dark mode) -->
          @if (isDarkMode()) {
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="4"/>
              <path d="M12 2v2"/>
              <path d="M12 20v2"/>
              <path d="m4.93 4.93 1.41 1.41"/>
              <path d="m17.66 17.66 1.41 1.41"/>
              <path d="M2 12h2"/>
              <path d="M20 12h2"/>
              <path d="m6.34 17.66-1.41 1.41"/>
              <path d="m19.07 4.93-1.41 1.41"/>
            </svg>
          }
          <!-- Moon icon (shown in light mode) -->
          @if (!isDarkMode()) {
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
            </svg>
          }
        </button>

        <!-- Not Authenticated: Show Login Button -->
        @if (!isAuthenticated()) {
          <button (click)="handleLogin()" class="flex items-center gap-1.5 text-sm font-medium px-5 py-2.5 rounded-full transition-all shadow-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20">
            <span>Log In</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M7 7h10v10"/>
              <path d="M7 17L17 7"/>
            </svg>
          </button>
        }

        <!-- Authenticated: Show User Menu -->
        @if (isAuthenticated()) {
          <div class="relative">
            <button (click)="toggleUserMenu()" class="flex items-center gap-2 px-3 py-2 rounded-full transition-all hover:bg-slate-100 dark:hover:bg-slate-800">
              <div class="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-medium">
                {{ userInitial() }}
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-500 dark:text-slate-400" [class.rotate-180]="userMenuOpen()">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </button>
            <!-- User Dropdown Menu -->
            @if (userMenuOpen()) {
              <div class="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-2 z-50">
                <!-- User Info -->
                <div class="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                  <p class="text-sm font-medium text-slate-900 dark:text-white">{{ userName() || 'User' }}</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ userEmail() }}</p>
                </div>
                <!-- Menu Items -->
                <div class="py-1">
                  <!-- Advisor Portal - for advisors/admins -->
                  @if (showAdvisorPortal) {
                    <button (click)="navigateToAdvisorPortal()" class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                      <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                      </svg>
                      <span>Advisor Portal</span>
                    </button>
                  }
                  <!-- Lender Portal - for lenders/admins -->
                  @if (showLenderPortal) {
                    <button (click)="navigateToLenderPortal()" class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                      <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                      </svg>
                      <span>Lender Portal</span>
                    </button>
                  }
                  <button (click)="navigateToSettings()" class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                    <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    <span>Settings</span>
                  </button>
                </div>
                <!-- Sign Out -->
                <div class="border-t border-slate-100 dark:border-slate-700 pt-1 mt-1">
                  <button (click)="signOut()" class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                    </svg>
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Mobile Menu Button -->
      <div class="md:hidden flex items-center gap-2">
        <!-- Mobile Theme Toggle -->
        <button
          (click)="toggleTheme()"
          class="p-2 rounded-full transition-all hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
          [attr.aria-label]="isDarkMode() ? 'Switch to light mode' : 'Switch to dark mode'">
          @if (isDarkMode()) {
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="4"/>
              <path d="M12 2v2"/>
              <path d="M12 20v2"/>
              <path d="m4.93 4.93 1.41 1.41"/>
              <path d="m17.66 17.66 1.41 1.41"/>
              <path d="M2 12h2"/>
              <path d="M20 12h2"/>
              <path d="m6.34 17.66-1.41 1.41"/>
              <path d="m19.07 4.93-1.41 1.41"/>
            </svg>
          }
          @if (!isDarkMode()) {
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
            </svg>
          }
        </button>
        <button type="button" (click)="toggleMobileMenu()" [attr.aria-label]="mobileMenuOpen ? 'Close menu' : 'Open menu'" [attr.aria-expanded]="mobileMenuOpen" class="p-2 text-slate-500 dark:text-slate-400">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="4" x2="20" y1="12" y2="12"/>
            <line x1="4" x2="20" y1="6" y2="6"/>
            <line x1="4" x2="20" y1="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Mobile Menu -->
    @if (mobileMenuOpen) {
      <div class="md:hidden border-t border-slate-100 dark:border-slate-700 py-4">
        <div class="flex flex-col gap-4">
          <a routerLink="/" (click)="mobileMenuOpen = false" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
            Home
          </a>
          <button (click)="scrollToSection('solutions')" class="text-left text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
            Credit Solutions
          </button>
          <button (click)="scrollToSection('pricing')" class="text-left text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
            Pricing
          </button>
          <button (click)="scrollToSection('about')" class="text-left text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
            About
          </button>
          <button (click)="scrollToSection('contact')" class="text-left text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
            Contact
          </button>
          <!-- Request Terms - hidden for lenders unless admin -->
          @if (showRequestTerms) {
            <button (click)="mobileMenuOpen = false; navigateToRequestTerms()" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white cursor-pointer text-left">
              Request Terms
            </button>
          }
          <!-- Advisor Portal - shown for advisors or admins -->
          @if (showAdvisorPortal) {
            <a routerLink="/advisor-portal" (click)="mobileMenuOpen = false" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
              Advisor Portal
            </a>
          }
          <!-- Lender Portal - shown for lenders or admins -->
          @if (showLenderPortal) {
            <a routerLink="/lender-portal" (click)="mobileMenuOpen = false" class="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-900 dark:hover:text-white">
              Lender Portal
            </a>
          }
          <!-- Mobile: Not Authenticated -->
          @if (!isAuthenticated()) {
            <div class="pt-4 border-t border-slate-100 dark:border-slate-700">
              <button (click)="handleLogin()" class="flex items-center gap-1.5 text-sm font-medium px-5 py-2.5 rounded-full transition-all shadow-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20 w-fit">
                <span>Log In</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M7 7h10v10"/>
                  <path d="M7 17L17 7"/>
                </svg>
              </button>
            </div>
          }
          <!-- Mobile: Authenticated -->
          @if (isAuthenticated()) {
            <div class="pt-4 border-t border-slate-100 dark:border-slate-700 space-y-2">
              <div class="flex items-center gap-3 py-2">
                <div class="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-medium">
                  {{ userInitial() }}
                </div>
                <div>
                  <p class="text-sm font-medium text-slate-900 dark:text-white">{{ userName() || 'User' }}</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">{{ userEmail() }}</p>
                </div>
              </div>
              <!-- Advisor Portal - for advisors/admins -->
              @if (showAdvisorPortal) {
                <button (click)="navigateToAdvisorPortal()" class="w-full text-left text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white py-2">
                  Advisor Portal
                </button>
              }
              <!-- Lender Portal - for lenders/admins -->
              @if (showLenderPortal) {
                <button (click)="navigateToLenderPortal()" class="w-full text-left text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white py-2">
                  Lender Portal
                </button>
              }
              <button (click)="navigateToSettings()" class="w-full text-left text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white py-2">
                Settings
              </button>
              <button (click)="signOut()" class="w-full text-left text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 py-2">
                Sign out
              </button>
            </div>
          }
        </div>
      </div>
    }
  </div>
</nav>

<!-- Backdrop for closing user menu when clicking outside -->
@if (userMenuOpen()) {
  <button type="button" (click)="closeUserMenu()" aria-label="Close user menu" class="fixed inset-0 z-40 cursor-default bg-transparent border-0 p-0"></button>
}
